import React, { useEffect, useState, useRef } from "react";
import { PageHeader } from "../components/PageHeader";
import api, { formatApiError, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Trash, MapPin, Camera, ArrowsClockwise, Buildings } from "@phosphor-icons/react";
import CustomFieldsSection, { CustomFieldsView } from "../components/CustomFieldsSection";
import SalesPerformancePanel from "../components/SalesPerformancePanel";

function isSalesDept(dept) {
  return String(dept || "").trim().toLowerCase() === "sales";
}

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [reports, setReports] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filterDept, setFilterDept] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    department: user?.department || "",
    title: "", tasks: "", achievements: "", blockers: "",
  });
  const [visits, setVisits] = useState([]); // repeater rows
  const [location, setLocation] = useState({ lat: null, lng: null, address: "" });
  const [locationImage, setLocationImage] = useState(null); // File
  const [locatingGps, setLocatingGps] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customFields, setCustomFields] = useState({});
  const fileRef = useRef(null);

  const load = async () => {
    const params = isAdmin && filterDept ? { department: filterDept } : {};
    const [{ data: reps }, { data: st }] = await Promise.all([
      api.get("/reports", { params }),
      api.get("/settings"),
    ]);
    setReports(reps);
    setDepartments((st.department || []).map((d) => d.value));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterDept]);

  const captureGps = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this browser");
      return;
    }
    setLocatingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation((l) => ({ ...l, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        toast.success("GPS captured");
        setLocatingGps(false);
      },
      (err) => {
        toast.error("Could not read GPS: " + err.message);
        setLocatingGps(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().slice(0, 10),
      department: user?.department || "", title: "", tasks: "", achievements: "", blockers: "",
    });
    setLocation({ lat: null, lng: null, address: "" });
    setLocationImage(null);
    setCustomFields({});
    setVisits([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isSalesDept(form.department)) {
      if (!locationImage) {
        toast.error("Sales report requires a location screenshot");
        return;
      }
      if (location.lat === null || location.lng === null) {
        toast.error("Please capture GPS coordinates first");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        location_lat: location.lat, location_lng: location.lng, location_address: location.address,
        custom_fields: customFields,
        visits: visits.filter((v) => Object.values(v).some((x) => (x || "").toString().trim())),
      };
      const { data: created } = await api.post("/reports", payload);
      if (locationImage) {
        const fd = new FormData();
        fd.append("file", locationImage);
        await api.post(`/reports/${created.id}/location-image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Report submitted");
      setShowForm(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this report?")) return;
    try { await api.delete(`/reports/${r.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const salesRequired = isSalesDept(form.department);

  return (
    <div>
      <PageHeader
        eyebrow="Daily Log"
        title="Department Reports"
        subtitle="Log daily work by department. Sales team submits location screenshot with GPS."
        actions={
          <button onClick={() => setShowForm(true)} data-testid="new-report-btn"
                  className="brand-btn px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
            <Plus size={16} weight="bold" /> New Report
          </button>
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <SalesPerformancePanel />

        {isAdmin && (
          <div className="flex items-center gap-3">
            <label className="label-caps">Filter Department</label>
            <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)}
                    className="border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="report-filter-dept">
              <option value="">All departments</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {reports.map((r) => <ReportCard key={r.id} r={r} user={user} isAdmin={isAdmin} onDelete={del} />)}
          {reports.length === 0 && (
            <div className="lg:col-span-2 text-center text-[#9CA3AF] py-16 border border-dashed border-[#E5E7EB]">No reports yet.</div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="report-form-modal">
          <div className="bg-white w-full max-w-2xl border border-[#E5E7EB] max-h-[90vh] overflow-y-auto">
            <div className="border-b border-[#E5E7EB] px-6 py-4">
              <div className="label-caps">Submit</div>
              <h3 className="font-display font-bold text-xl">Daily Report</h3>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-1.5">Date</label>
                  <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Department</label>
                  <select required value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="report-form-dept">
                    <option value="">Select…</option>
                    {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label-caps block mb-1.5">Title</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="report-form-title" />
              </div>
              <div>
                <label className="label-caps block mb-1.5">Tasks Done Today</label>
                <textarea required rows={4} value={form.tasks} onChange={(e) => setForm({ ...form, tasks: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="report-form-tasks" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-1.5">Achievements</label>
                  <textarea rows={3} value={form.achievements} onChange={(e) => setForm({ ...form, achievements: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Blockers</label>
                  <textarea rows={3} value={form.blockers} onChange={(e) => setForm({ ...form, blockers: e.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" />
                </div>
              </div>

              {/* Location & screenshot — required for Sales */}
              <div className={`border ${salesRequired ? "border-[#106109] bg-[#F4F6FC]" : "border-[#E5E7EB]"} p-4 space-y-3`} data-testid="report-location-section">
                <div className="flex items-center gap-2">
                  <MapPin size={18} color="#106109" weight="fill" />
                  <div>
                    <div className="label-caps">Location {salesRequired ? "· Required for Sales" : "· Optional"}</div>
                    <div className="text-xs text-[#6B7280]">Capture GPS and upload a screenshot of your current location (Google Maps, etc.)</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <button type="button" onClick={captureGps} disabled={locatingGps} data-testid="capture-gps-btn"
                          className="px-3 py-2 text-sm border border-[#E5E7EB] hover:bg-white inline-flex items-center gap-2">
                    <ArrowsClockwise size={14} className={locatingGps ? "animate-spin" : ""} />
                    {locatingGps ? "Locating…" : (location.lat !== null ? "Refresh GPS" : "Capture GPS")}
                  </button>
                  {location.lat !== null && (
                    <div className="text-xs font-mono text-[#111827] bg-white border border-[#E5E7EB] px-3 py-2" data-testid="report-gps-display">
                      {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                    </div>
                  )}
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Address / place name (optional)</label>
                  <input value={location.address} onChange={(e) => setLocation({ ...location, address: e.target.value })}
                         placeholder="e.g., Client office — Bandra West, Mumbai"
                         className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="report-address" />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Location screenshot {salesRequired && <span className="text-[#DC2626]">*</span>}</label>
                  <label className="border border-dashed border-[#E5E7EB] px-3 py-3 flex items-center justify-between cursor-pointer hover:bg-white transition-colors duration-200">
                    <div className="flex items-center gap-2 text-sm">
                      <Camera size={16} />
                      <span>{locationImage ? locationImage.name : "Choose a JPG / PNG / WebP (max 8MB)"}</span>
                    </div>
                    {locationImage && (
                      <button type="button" onClick={(e) => { e.preventDefault(); setLocationImage(null); if (fileRef.current) fileRef.current.value = ""; }}
                              className="text-xs text-[#DC2626]">Remove</button>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="report-location-image"
                           onChange={(e) => setLocationImage(e.target.files[0] || null)} />
                  </label>
                </div>
              </div>

              <CustomFieldsSection entity="report" values={customFields} onChange={setCustomFields} />

              <VisitsRepeater visits={visits} onChange={setVisits} />

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="flex-1 py-2.5 text-sm border border-[#E5E7EB]">Cancel</button>
                <button type="submit" disabled={submitting} className="brand-btn flex-1 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="report-form-submit">
                  {submitting ? "Submitting…" : "Submit Report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportCard({ r, user, isAdmin, onDelete }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [loadingImg, setLoadingImg] = useState(false);

  const loadImage = async () => {
    if (imgUrl) { setImgUrl(null); return; }
    setLoadingImg(true);
    try {
      const res = await api.get(`/reports/${r.id}/location-image`, { responseType: "blob" });
      setImgUrl(URL.createObjectURL(res.data));
    } catch { toast.error("Could not load image"); }
    finally { setLoadingImg(false); }
  };

  const hasLocation = r.location_lat != null && r.location_lng != null;
  const mapUrl = hasLocation ? `https://www.google.com/maps?q=${r.location_lat},${r.location_lng}` : null;

  return (
    <div className="bg-white border border-[#E5E7EB] p-6" data-testid={`report-card-${r.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-caps">{r.department} · {r.date}</div>
          <h3 className="font-display font-bold text-xl mt-1">{r.title}</h3>
          <div className="text-xs text-[#6B7280] mt-1">{r.employee_name} ({r.employee_code})</div>
        </div>
        {(isAdmin || r.employee_id === user?.id) && (
          <button onClick={() => onDelete(r)} data-testid={`delete-report-${r.id}`} className="border border-[#E5E7EB] p-1.5 hover:bg-[#FEF2F2] text-[#DC2626]"><Trash size={14} /></button>
        )}
      </div>
      <div className="mt-4 space-y-3 text-sm">
        <Section label="Tasks" text={r.tasks} />
        {r.achievements && <Section label="Achievements" text={r.achievements} />}
        {r.blockers && <Section label="Blockers" text={r.blockers} />}
      </div>

      {(hasLocation || r.location_address || r.location_image_path) && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={14} color="#106109" weight="fill" />
            <div className="label-caps">Location</div>
          </div>
          {r.location_address && <div className="text-sm text-[#111827]">{r.location_address}</div>}
          {hasLocation && (
            <a href={mapUrl} target="_blank" rel="noopener noreferrer"
               className="text-xs font-mono text-[#106109] hover:underline" data-testid={`report-map-link-${r.id}`}>
              {r.location_lat.toFixed(6)}, {r.location_lng.toFixed(6)} ↗
            </a>
          )}
          {r.location_image_path && (
            <div className="mt-2">
              <button onClick={loadImage} className="text-xs text-[#106109] hover:underline" data-testid={`toggle-image-${r.id}`}>
                {loadingImg ? "Loading…" : imgUrl ? "Hide screenshot" : "Show screenshot"}
              </button>
              {imgUrl && (
                <div className="mt-2 border border-[#E5E7EB]">
                  <img src={imgUrl} alt="Location screenshot" className="max-h-64 w-auto"
                       data-testid={`report-image-${r.id}`} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <CustomFieldsView entity="report" values={r.custom_fields} />
      <VisitsTable visits={r.visits} />
    </div>
  );
}

const EMPTY_VISIT = {
  visit_point: "", person_name: "", business_name: "", location: "",
  phone: "", purpose: "", remarks: "", next_followup_date: "",
};

const VISIT_COLS = [
  { key: "visit_point", label: "Visit Point", w: "140px", placeholder: "Client office / Site" },
  { key: "person_name", label: "Person Visited", w: "130px", placeholder: "Rahul Sharma" },
  { key: "business_name", label: "Business", w: "140px", placeholder: "ABC Traders" },
  { key: "location", label: "Location", w: "150px", placeholder: "Bandra West, Mumbai" },
  { key: "phone", label: "Phone", w: "120px", placeholder: "+91 98…" },
  { key: "purpose", label: "Purpose", w: "150px", placeholder: "Product demo" },
  { key: "remarks", label: "Remarks", w: "170px", placeholder: "Interested, sending quote" },
  { key: "next_followup_date", label: "Next Follow-up", w: "140px", type: "date" },
];

function VisitsRepeater({ visits, onChange }) {
  const add = () => onChange([...(visits || []), { ...EMPTY_VISIT }]);
  const remove = (i) => onChange(visits.filter((_, idx) => idx !== i));
  const setCell = (i, key, value) => {
    const next = visits.map((v, idx) => (idx === i ? { ...v, [key]: value } : v));
    onChange(next);
  };
  return (
    <div className="border border-[#E5E7EB] p-4" data-testid="visits-repeater">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Buildings size={18} color="#106109" weight="fill" />
          <div>
            <div className="label-caps">Field visits · Optional</div>
            <div className="text-xs text-[#6B7280]">Log every visit point you covered today.</div>
          </div>
        </div>
        <button type="button" onClick={add} data-testid="add-visit-row"
                className="brand-btn text-xs px-3 py-1.5 inline-flex items-center gap-1.5">
          <Plus size={12} weight="bold" /> Add visit
        </button>
      </div>
      {visits.length === 0 ? (
        <div className="border border-dashed border-[#E5E7EB] py-6 text-center text-xs text-[#9CA3AF]">
          No visits added. Click "Add visit" for each field call you made today.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border border-[#F3F4F6]">
            <thead className="bg-[#F9FAFB]">
              <tr>
                <th className="px-2 py-2 label-caps text-[10px] text-left w-8">#</th>
                {VISIT_COLS.map((c) => (
                  <th key={c.key} className="px-2 py-2 label-caps text-[10px] text-left" style={{ minWidth: c.w }}>{c.label}</th>
                ))}
                <th className="px-2 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {visits.map((v, i) => (
                <tr key={i} className="border-t border-[#F3F4F6]" data-testid={`visit-row-${i}`}>
                  <td className="px-2 py-1 text-[#6B7280] font-mono">{i + 1}</td>
                  {VISIT_COLS.map((c) => (
                    <td key={c.key} className="px-1 py-1" style={{ minWidth: c.w }}>
                      <input
                        type={c.type || "text"}
                        value={v[c.key] || ""}
                        placeholder={c.placeholder || ""}
                        onChange={(e) => setCell(i, c.key, e.target.value)}
                        data-testid={`visit-${i}-${c.key}`}
                        className="w-full border border-transparent hover:border-[#E5E7EB] focus:border-[#106109] px-2 py-1.5 text-xs focus:outline-none"
                      />
                    </td>
                  ))}
                  <td className="px-1 py-1 text-right">
                    <button type="button" onClick={() => remove(i)} data-testid={`remove-visit-${i}`}
                            className="p-1.5 text-[#DC2626] hover:bg-[#FEF2F2]" title="Remove row">
                      <Trash size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VisitsTable({ visits }) {
  if (!visits || visits.length === 0) return null;
  return (
    <div className="mt-4 border-t border-[#E5E7EB] pt-4" data-testid="report-visits">
      <div className="flex items-center gap-2 mb-2">
        <Buildings size={14} color="#106109" weight="fill" />
        <div className="label-caps">Field visits · {visits.length}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-[#F3F4F6]">
          <thead className="bg-[#F9FAFB]">
            <tr>
              <th className="px-2 py-2 label-caps text-[10px] text-left w-6">#</th>
              {VISIT_COLS.map((c) => (
                <th key={c.key} className="px-2 py-2 label-caps text-[10px] text-left whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visits.map((v, i) => (
              <tr key={i} className="border-t border-[#F3F4F6]">
                <td className="px-2 py-1.5 text-[#6B7280] font-mono">{i + 1}</td>
                {VISIT_COLS.map((c) => (
                  <td key={c.key} className="px-2 py-1.5 whitespace-nowrap text-[#111827]">
                    {v[c.key] || <span className="text-[#D1D5DB]">—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ label, text }) {
  return (
    <div>
      <div className="label-caps text-[10px]">{label}</div>
      <div className="text-sm text-[#111827] mt-1 whitespace-pre-wrap">{text}</div>
    </div>
  );
}
